'use client'

// Drop-in replacement for `import { toast } from 'sonner'` that also records
// error toasts in the UI action log. An error toast is the closest record of
// what the user actually SAW when something failed, so it belongs in the same
// session timeline as their clicks and navigation (lib/ui-log). Only STRING
// error messages are logged — ReactNode messages are shown but not logged —
// and the logging path can never break or change what sonner returns.
// Everything else in sonner's API passes through unchanged; an ESLint
// no-restricted-imports rule keeps app code on this module.

import { toast as sonnerToast } from 'sonner'
import { logUiEvent, scrubLabel } from '@/lib/ui-log'

export { Toaster, useSonner } from 'sonner'
export type {
  Action,
  ExternalToast,
  ToastClassnames,
  ToastT,
  ToastToDismiss,
  ToasterProps,
} from 'sonner'

type SonnerToast = typeof sonnerToast

const error: SonnerToast['error'] = (message, data) => {
  try {
    if (typeof message === 'string') {
      logUiEvent(
        'toast.error',
        { message: scrubLabel(message, 160) },
        { coalesceKey: message.slice(0, 80) },
      )
    }
  } catch {
    // Logging must never block the toast itself.
  }
  return sonnerToast.error(message, data)
}

// sonner exports `toast` as a plain function with its methods Object.assign'd
// on (no `this` usage), so copying those methods onto a forwarding callable
// and swapping `error` keeps success/info/warning/message/promise/custom/
// dismiss/loading/getHistory/getToasts bound to the originals while
// `toast('plain message')` still works through the callable base.
export const toast: SonnerToast = Object.assign(
  ((message, data) => sonnerToast(message, data)) as SonnerToast,
  sonnerToast,
  { error },
)
